'use client';

import React, { memo, useRef, useState } from 'react';
import { Card, Text, BlockStack, InlineStack, Button, Bleed, Box, Icon } from '@shopify/polaris';
import { Image } from '@/components/Image';
import CardTag from '../ai-tools/CardTag';
import { TemplateTag } from './types/template';
import { Template } from './types/template';
import { cn } from '@/lib/utils';

interface TemplateCardProps {
  template: Template;
  onAction: (template: Template) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onAction }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Card>
      <Bleed marginBlockStart="400" marginInline="400">
        <div
          onMouseEnter={() => {
            if (videoRef.current) {
              videoRef.current.play();
            }
            setIsHovering(true);
          }}
          onMouseLeave={() => {
            if (videoRef.current) {
              videoRef.current.pause();
            }
            setIsHovering(false);
          }}
        >
          {template.videoUrl ? (
            <Bleed marginBlockStart="400" marginInline="400">
              <div className="relative">
                <video
                  poster={template.image}
                  ref={videoRef}
                  className={cn(
                    'h-[367px] w-full rounded-t-md object-cover',
                    template.videoPosition || 'object-center'
                  )}
                  loop
                  muted
                >
                  <source
                    src={template.image ? `${template.videoUrl}` : `${template.videoUrl}#t=0.1`}
                    type="video/mp4"
                  />
                </video>
                {template.videoDuration && (
                  <div className="absolute bottom-4 left-6 rounded bg-black bg-opacity-50 px-2 py-1 text-sm text-white">
                    {template.videoDuration}
                  </div>
                )}
              </div>
            </Bleed>
          ) : template.image ? (
            <Bleed marginBlockStart="400" marginInline="400">
              <div className="relative">
                <Image
                  className="h-[367px] w-full rounded-t-md object-top"
                  src={template.image}
                  alt={template.title}
                  size="medium"
                  objectFit="cover"
                  unoptimized
                />
                {isHovering && (
                  <div className="absolute bottom-4 left-6 rounded bg-black bg-opacity-50 px-2 py-1">IMG</div>
                )}
              </div>
            </Bleed>
          ) : null}
          <Box padding="400">
            <BlockStack gap="400">
              <Text as="p" variant="bodyLg" fontWeight="medium">
                {template.title}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {template.description}
              </Text>
              <InlineStack align="space-between">
                <div>
                  {template.tags?.includes?.(TemplateTag.Fashion) && (
                    <InlineStack gap="200" align="start" blockAlign="start">
                      <CardTag tag={TemplateTag.Fashion} />
                    </InlineStack>
                  )}
                </div>
                <Button onClick={() => onAction(template)} size="slim">
                  Use template
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        </div>
      </Bleed>
    </Card>
  );
};

export default memo(TemplateCard);
